import React, { useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';

import Icon from 'dpl/common/components/Icon';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import BreederProfileHelpBoxContainer from 'dpl/containers/BreederProfile/Index/BreederProfileHelpBoxContainer';
import Paginator from 'dpl/common/components/pagination/components/Paginator';
import BreederProfileRelatedBreedsListContainer from 'dpl/containers/BreederProfile/Index/BreederProfileRelatedBreedsListContainer';
import BreederProfileNearbyCities from 'dpl/components/BreederProfile/Index/BreederProfileList/BreederProfileNearbyCities';
import BreederProfileNearbyStates from 'dpl/components/BreederProfile/Index/BreederProfileList/BreederProfileNearbyStates';
import BreederProfilePopularCities from 'dpl/components/BreederProfile/Index/BreederProfileList/BreederProfilePopularCities';
import BreederProfilePopularStates from 'dpl/components/BreederProfile/Index/BreederProfileList/BreederProfilePopularStates';
import FilterPanel from 'dpl/breeder_search_results/components/FilterPanel';
import FrequentlyAskedQuestions from 'dpl/breeder_search_results/components/FrequentlyAskedQuestions';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import { scrollTo, isMobileUA } from 'dpl/shared/utils';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useInfiniteCollectionQuery from 'dpl/shared/fetching/hooks/useInfiniteCollectionQuery';
import { breederProfilesResourceDefinition } from 'dpl/breeder_search_results/utils/queryDefinitions';
import {
  puppiesResourceDefinition,
  puppyAndBreederCountsResourceDefinition
} from 'dpl/puppy_search_results/utils/queryDefinitions';
import {
  PAGE,
  FILTER_POSTAL_CODE,
  FILTER_DISTANCE_IN_MILES,
  FILTER_AVAILABILITY,
  FILTER_DELIVERY_METHODS,
  FILTER_SIZE,
  FILTER_COLOR,
  FILTER_COAT,
  FILTER_TAIL,
  FILTER_GENERATION,
  RESULTS_TYPE,
  FILTER_GENDER,
  FILTER_HEALTH_TIER_LEVELS,
  FILTER_PRICE,
  SORT,
  UTM_MEDIUM
} from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { removeFromUrl } from 'dpl/util/queryString';
import { ANALYTICS_EVENTS, ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import { GDButtonV1 } from 'dpl/common/core/GDButton/GDButtonV1';
import Button from 'dpl/common/design_system/Button';
import {
  sendRudderstackEvent,
  collectTrackedExperiments
} from 'dpl/util/analytics';
import BreederCard from 'dpl/breeder_search_results/components/Card';
import ResultsTypeSelector from 'dpl/puppy_search_results/components/ResultsTypeSelector';
import ResultsTypeSelectorV2 from 'dpl/puppy_search_results/components/v2/ResultsTypeSelector';
import SearchResults from 'dpl/puppy_search_results/components/SearchResults';
import SortDropdown from 'dpl/puppy_search_results/components/SortDropdown';
import {
  RESULTS_TYPES,
  NON_LOCATION_SORT_OPTIONS,
  FILTER_PARAMS
} from 'dpl/puppy_search_results/utils/constants';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import BreederProfileBreedOverviewPages from 'dpl/components/BreederProfile/Index/BreederProfileList/BreederProfileBreedOverviewPages';
import { GB_FEATURES } from 'dpl/shared/constants/gb_features';

import ItemCount from './ItemCount';
import PuppyCard from './PuppyCard';

const IS_BOT_UA = window.__DPL_BOT_UA;

const IS_LT_LG = isLessThanBreakpoint('lg');

const IS_MOBILE_UA = isMobileUA();
const BROWSE_PARAMS = [PAGE, SORT, ...FILTER_PARAMS];

function mapParamsBreedAttributes(paramValues = '', breedAttributes) {
  return paramValues
    .split(',')
    .map(p => breedAttributes.find(({ value }) => value === Number(p)))
    .filter(p => p);
}

function setSearchQueryDefinition(resultsType) {
  return resultsType === RESULTS_TYPES.PUPPIES
    ? puppiesResourceDefinition
    : breederProfilesResourceDefinition;
}

export default function TabbedLayout() {
  return (
    <FeaturesReady timeout={500}>
      {IS_MOBILE_UA ? <GrowthbookComponent /> : <Component />}
    </FeaturesReady>
  );
}

function GrowthbookComponent() {
  const expInfiniteScrollValue = useFeatureValue(
    GB_FEATURES.EXP_SEARCH_INFINITE_SCROLL_V2,
    'control'
  );

  return <Component expInfiniteScrollValue={expInfiniteScrollValue} />;
}

function PuppyResultCard({ result, analytics, hideDistance, trackInView }) {
  return (
    <PuppyCard
      puppy={result}
      analytics={analytics}
      hideDistance={hideDistance}
      view={ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS}
      trackInView={trackInView}
    />
  );
}

PuppyResultCard.propTypes = {
  result: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  hideDistance: PropTypes.bool.isRequired,
  trackInView: PropTypes.func.isRequired
};

function BreederResultCard({ result, analytics, trackInView }) {
  return (
    <BreederCard
      profile={result}
      analytics={analytics}
      trackInView={trackInView}
    />
  );
}

BreederResultCard.propTypes = {
  result: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  trackInView: PropTypes.func.isRequired
};

function Component({ expInfiniteScrollValue }) {
  const {
    breedSlug,
    itemsPerPage,
    overPopulatedBreed,
    breedId,
    breedName,
    breedNamePlural,
    citySlug,
    locationTitle,
    breedColors,
    puppyPriceRanges,
    breedSizes,
    breedCoat,
    breedTail,
    breedGeneration,
    preappliedFilters: { stateSlug, cityName, size } = {},
    pbo: serverPbo,
    nonStandard
  } = useSelector(({ server }) => server);

  const isInfiniteScroll = expInfiniteScrollValue !== 'control';

  const paidPage = useSelector(({ queryParams }) => {
    return ['cpc', 'cpa', 'cta'].includes(queryParams[UTM_MEDIUM]);
  });

  const params = useSelector(({ queryParams }) => queryParams);
  const prevParamsRef = useRef(params);

  const currentPage = Number(params[PAGE]) || 1;
  const initialResultType = IS_BOT_UA
    ? RESULTS_TYPES.BREEDERS
    : RESULTS_TYPES.PUPPIES;
  const resultsType = params[RESULTS_TYPE] || initialResultType;

  // if pbo, only show breeders with payment activity
  const pbo = serverPbo || paidPage;

  const dispatch = useDispatch();

  const searchQueryDefinition = setSearchQueryDefinition(resultsType);
  const profilesQueryDefinition = {
    ...searchQueryDefinition(
      breedSlug,
      itemsPerPage,
      // NOTE: gets overriden when useInfiniteCollectionQuery is in use
      currentPage,
      removeEmptyValues({
        postal_code: params[FILTER_POSTAL_CODE],
        state: stateSlug,
        city: cityName,
        distance_in_miles:
          params[FILTER_DISTANCE_IN_MILES] ||
          /* If using a non-location sort option without a distance value,
             set `distance_in_miles` to 0 so that a default location range
             is not applied by the API */
          (NON_LOCATION_SORT_OPTIONS.includes(params[SORT]) ? 0 : null),
        availability: params[FILTER_AVAILABILITY],
        delivery_method_ids: params[FILTER_DELIVERY_METHODS],
        size: params[FILTER_SIZE] || size,
        color: params[FILTER_COLOR],
        coat_type: params[FILTER_COAT],
        generation: params[FILTER_GENERATION],
        non_standard: nonStandard,
        pbo,
        city_slug: citySlug,
        // Breeders only
        ...(resultsType === RESULTS_TYPES.BREEDERS && {
          tail_type: params[FILTER_TAIL],
          health_tier_levels: params[FILTER_HEALTH_TIER_LEVELS]
        }),

        // Puppies only
        ...(resultsType === RESULTS_TYPES.PUPPIES && {
          gender:
            params[FILTER_GENDER] !== 'any' ? params[FILTER_GENDER] : null,
          price_tier: params[FILTER_PRICE],
          sort: params[SORT]
        })
      })
    ),
    keepPreviousData: true
  };

  const infiniteProfilesQuery = useInfiniteCollectionQuery({
    ...profilesQueryDefinition,
    enabled: isInfiniteScroll
  });

  const paginatedProfilesQuery = useResourceQuery({
    ...profilesQueryDefinition,
    enabled: !isInfiniteScroll
  });

  // tab counts should only reflect filtering based on PBO and non_standard
  const countsQuery = useResourceQuery(
    puppyAndBreederCountsResourceDefinition(breedSlug, {
      non_standard: nonStandard,
      pbo
    })
  );

  const [profilesMeta, profilesData, profilesQuery] = useMemo(() => {
    if (isInfiniteScroll) {
      const pages = infiniteProfilesQuery.data?.pages || [];

      return [
        pages[pages.length - 1]?.data.meta || {},
        infiniteProfilesQuery.infiniteScrollItems,
        infiniteProfilesQuery
      ];
    }

    return [
      paginatedProfilesQuery.value.meta,
      paginatedProfilesQuery.value.data,
      paginatedProfilesQuery
    ];
  }, [
    isInfiniteScroll,
    infiniteProfilesQuery.infiniteScrollItems,
    paginatedProfilesQuery.value?.data
  ]);

  const breedersTabCount = countsQuery.value.data.breeders_count;
  const puppiesTabCount = countsQuery.value.data.puppies_count;
  const totalPuppiesCount = profilesMeta.total_puppy_count;
  const totalBreedersCount = profilesMeta.total_breeders_count;
  const totalItemCount =
    (resultsType === RESULTS_TYPES.PUPPIES
      ? totalPuppiesCount
      : totalBreedersCount) || 0;
  const totalPuppiesNearbyCount = profilesMeta.total_puppies_nearby_count;
  const hasPuppiesNearby =
    resultsType === RESULTS_TYPES.PUPPIES && totalPuppiesNearbyCount > 0;
  const availabilityCountText = profilesMeta.tab_availability_count_text;
  const shouldShowAvailabilityCountText =
    !IS_BOT_UA &&
    currentPage === 1 &&
    totalItemCount > 0 &&
    (hasPuppiesNearby || resultsType === RESULTS_TYPES.BREEDERS);
  const numberOfPages = Math.ceil(totalItemCount / itemsPerPage);
  const isContinentalState = !['hawaii', 'alaska'].includes(stateSlug);
  const distanceInMiles = profilesMeta.distance_in_miles;
  const shouldShowSortDropdown = resultsType === RESULTS_TYPES.PUPPIES;
  const hasNoItemsDueToFilters =
    totalItemCount === 0 &&
    ((puppiesTabCount > 0 && resultsType === RESULTS_TYPES.PUPPIES) ||
      (breedersTabCount > 0 && resultsType === RESULTS_TYPES.BREEDERS));

  const isEmptyPage = !!(totalItemCount && currentPage > numberOfPages);

  if (isEmptyPage) {
    const firstPage = removeFromUrl(PAGE, window.location.href);
    window.location.href = firstPage;
  }

  if (resultsType === RESULTS_TYPES.PUPPIES && puppiesTabCount === 0) {
    dispatch(
      pushParams({ [RESULTS_TYPE]: RESULTS_TYPES.BREEDERS }, { hash: true })
    );
  }

  const ref = useRef(null);

  function scrollToTab() {
    scrollTo({ top: ref.current.offsetTop - 60 });
  }

  function getPosition(idx) {
    let position = idx + 1;
    if (currentPage > 1) {
      position += (currentPage - 1) * itemsPerPage;
    }

    return position;
  }

  function breederProfilesForTracking(breederProfiles) {
    return breederProfiles.map((breeder, idx) => {
      const transportation = Object.keys(breeder.delivery_methods || {}).reduce(
        (allTypes, key) => {
          return [
            ...allTypes,
            ...breeder.delivery_methods[key].map(dm => dm.name)
          ];
        },
        []
      );

      return {
        breeder_profile_id: breeder.id,
        breeder_name: breeder.name,
        puppies_available: breeder.available_puppy_count,
        position: getPosition(idx),
        transportation,
        miles_away: breeder.distance_in_miles
      };
    });
  }

  function puppiesForTracking(puppies) {
    return puppies.map((puppy, idx) => {
      const transportation = Object.keys(
        puppy.breeder_profile.delivery_methods || {}
      ).reduce((allTypes, key) => {
        return [
          ...allTypes,
          ...puppy.breeder_profile.delivery_methods[key].map(dm => dm.name)
        ];
      }, []);

      return {
        puppy_id: puppy.id,
        puppy_name: puppy.name,
        breeder_profile_id: puppy.breeder_profile.id,
        breeder_name: puppy.breeder_profile.name,
        position: getPosition(idx),
        transportation,
        miles_away: puppy.distance_in_miles
      };
    });
  }

  function clearFilters() {
    dispatch(resetParams(PAGE, ...FILTER_PARAMS, { hash: true }));
  }

  const analyticsPageNumber = isInfiniteScroll
    ? infiniteProfilesQuery.data?.pages.length || 1
    : currentPage;

  function sendBreedSearchedAnalytics() {
    const view =
      resultsType === RESULTS_TYPES.PUPPIES
        ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
        : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

    setAnalyticsOrigin({ originView: view });
    const eventData = {
      page: 'breed_page',
      breed_id: breedId,
      breed_name: breedName,
      experiments: collectTrackedExperiments(),
      results_size_breeders: totalBreedersCount,
      results_size_available_puppies: totalPuppiesCount,
      page_number: analyticsPageNumber,
      results_display_type: resultsType,
      filters: {
        postal_code: params[FILTER_POSTAL_CODE],
        state: stateSlug,
        city: cityName,
        distance_in_miles: params[FILTER_DISTANCE_IN_MILES],
        availability: params[FILTER_AVAILABILITY],
        delivery_method_ids: params[FILTER_DELIVERY_METHODS],
        size: mapParamsBreedAttributes(params[FILTER_SIZE], breedSizes),
        color: mapParamsBreedAttributes(
          params[FILTER_COLOR],
          breedColors.reduce((c, [, opts]) => [...c, ...opts], [])
        ),
        coat_type: mapParamsBreedAttributes(params[FILTER_COAT], breedCoat),
        tail_type: mapParamsBreedAttributes(params[FILTER_TAIL], breedTail),
        generation: mapParamsBreedAttributes(
          params[FILTER_GENERATION],
          breedGeneration
        ),
        // Puppies only
        ...(resultsType === RESULTS_TYPES.PUPPIES && {
          gender: params[FILTER_GENDER],
          price:
            (Boolean(puppyPriceRanges) &&
              params[FILTER_PRICE] &&
              puppyPriceRanges.find(p => p.value === params[FILTER_PRICE])
                ?.title) ||
            null
        })
      },
      // Puppies only
      ...(resultsType === RESULTS_TYPES.PUPPIES && {
        sort: params[SORT]
      })
    };

    if (resultsType === RESULTS_TYPES.PUPPIES) {
      eventData.puppies = puppiesForTracking(profilesData);
    } else {
      eventData.breeders = breederProfilesForTracking(profilesData);
    }

    sendRudderstackEvent(ANALYTICS_EVENTS.BREED_SEARCHED, eventData);
  }

  useEffect(() => {
    if (profilesQuery.status !== 'success') {
      return;
    }

    sendBreedSearchedAnalytics();
  }, [profilesQuery.status, profilesQuery.dataUpdatedAt]);

  useEffect(() => {
    const shouldScroll = BROWSE_PARAMS.some(
      param => params[param] !== prevParamsRef.current[param]
    );

    if (shouldScroll) {
      scrollToTab();
    }

    prevParamsRef.current = params;
  }, [params]);

  function trackPaginationEvent() {
    sendRudderstackEvent(ANALYTICS_EVENTS.SEARCH_PAGINATED, {
      view:
        resultsType === RESULTS_TYPES.PUPPIES
          ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
          : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS,
      breed_id: breedId,
      page_number: analyticsPageNumber + 1
    });
  }

  function setPage(page) {
    dispatch(pushParams({ [PAGE]: page }));

    if (page > currentPage) {
      trackPaginationEvent();
    }
  }

  async function loadMore() {
    if (infiniteProfilesQuery.isFetchingNextPage) {
      return;
    }

    await infiniteProfilesQuery.fetchNextPage();
    trackPaginationEvent();
  }

  return (
    <>
      {IS_MOBILE_UA && (
        <ResultsTypeSelectorV2
          resultsType={resultsType}
          locked={puppiesTabCount === 0}
        />
      )}
      <div className="PuppyResultsTabbedLayout container">
        <div className="row mb18 mb20-lg">
          <div className="col-lg-3">
            {IS_MOBILE_UA ? (
              <>
                <FilterPanel
                  shouldShowSort={shouldShowSortDropdown}
                  resultsType={resultsType}
                  totalItemCount={totalItemCount}
                />
                <ItemCount
                  puppiesCount={puppiesTabCount}
                  breedersCount={breedersTabCount}
                  resultsType={resultsType}
                />
              </>
            ) : (
              <FilterPanel
                shouldShowSort={shouldShowSortDropdown}
                resultsType={resultsType}
                totalItemCount={totalItemCount}
              />
            )}
          </div>
          <div ref={ref} className="col-lg-9">
            {!IS_MOBILE_UA && (
              <div className="mb6 mb9-md br2 row">
                <div className="w-100 col-12 col-lg-9">
                  <ResultsTypeSelector
                    puppiesCount={puppiesTabCount}
                    breedersCount={breedersTabCount}
                    resultsType={resultsType}
                    locked={puppiesTabCount === 0}
                  />
                </div>
                {/* only show sort dropdown on larger screens, otherwise combine with filter panel */}
                {shouldShowSortDropdown && !IS_LT_LG && (
                  <div className="col-12 col-lg-3">
                    <SortDropdown className="fw-medium" />
                  </div>
                )}
              </div>
            )}
            <LoadingWrapper
              isLoading={
                profilesQuery.isLoading ||
                profilesQuery.isPreviousData ||
                isEmptyPage
              }
            >
              {!puppiesTabCount && (
                <div className="flex pb12 col-12 col-lg-9">
                  <Icon
                    className="black"
                    name="puppy-search"
                    height="56px"
                    width="66px"
                  />
                  <div className="pl4">
                    <p className="b">
                      Work with a breeder to get a spot in an upcoming litter
                    </p>
                    <p className="f2">
                      There are no {breedName} puppies available right now. Find
                      and apply to Good Dog breeders to learn about upcoming
                      litters.
                    </p>
                  </div>
                </div>
              )}
              {hasNoItemsDueToFilters && (
                <div className="f2 f3-sm tc bg-near-white br3 ph4 pv4">
                  <p className="fw-medium">
                    Your current search has no results
                  </p>
                  <p className="mb4">
                    Clear your filters to find puppies and breeders
                  </p>
                  <GDButtonV1
                    className="f2 m-auto"
                    onClick={clearFilters}
                    type="reset"
                  >
                    Clear filters
                  </GDButtonV1>
                </div>
              )}
              {shouldShowAvailabilityCountText && (
                <p className="mt8 dn db-l">{availabilityCountText}</p>
              )}
              <SearchResults
                className="mt3"
                scrollToTab={scrollToTab}
                results={profilesData}
                distanceInMiles={distanceInMiles}
                totalItemCount={totalItemCount}
                analyticsPageNumber={analyticsPageNumber}
                Card={
                  resultsType === RESULTS_TYPES.PUPPIES
                    ? PuppyResultCard
                    : BreederResultCard
                }
              />
              {overPopulatedBreed && (
                <div className="pt5 pb5 bt bb b--light-gray bw0-l mt5 mb5">
                  <BreederProfileHelpBoxContainer
                    breedName={breedName}
                    breedNamePlural={breedNamePlural}
                  />
                </div>
              )}
              {isInfiniteScroll
                ? profilesQuery.hasNextPage && (
                    <InView
                      className="pt4"
                      rootMargin={`${window.outerHeight * 4}px 0px`}
                      onChange={inView => {
                        if (expInfiniteScrollValue === 'variant_2' && inView) {
                          loadMore();
                        }
                      }}
                    >
                      <Button
                        type="button"
                        variant="primary"
                        disabled={profilesQuery.isFetchingNextPage}
                        onClick={loadMore}
                        className="w-100"
                      >
                        Load more
                      </Button>
                    </InView>
                  )
                : numberOfPages > 1 && (
                    <div className="pt6-lg">
                      {currentPage < numberOfPages && (
                        <p className="f2 tc">
                          View more {breedName} {resultsType}
                        </p>
                      )}
                      <div className="pt2">
                        <Paginator
                          currentPage={currentPage}
                          itemsPerPage={itemsPerPage}
                          onPageClick={setPage}
                          totalItemCount={totalItemCount}
                        />
                      </div>
                    </div>
                  )}
            </LoadingWrapper>
          </div>
        </div>
        <div className="mb6 mb20-lg">
          <BreederProfileRelatedBreedsListContainer />
        </div>
        {citySlug && currentPage === 1 && (
          <div className="mb6 mb20-lg">
            <FrequentlyAskedQuestions />
          </div>
        )}
        <div className="BreederRelatedLinks">
          {stateSlug && !citySlug && (
            <div className={classnames({ 'mb10 mb0-lg': isContinentalState })}>
              <BreederProfileNearbyCities
                breedName={breedName}
                breedId={breedId}
                wideLayout
                isStatePage
              />
              {isContinentalState && (
                <div className="">
                  <BreederProfileNearbyStates
                    breedName={breedName}
                    stateName={locationTitle}
                    breedId={breedId}
                    wideLayout
                    isStatePage
                  />
                </div>
              )}
            </div>
          )}
          {citySlug && (
            <BreederProfileNearbyCities
              breedName={breedName}
              breedId={breedId}
              wideLayout
            />
          )}
          {!locationTitle && (
            <>
              <BreederProfilePopularCities
                breedName={breedName}
                breedId={breedId}
              />
              <BreederProfilePopularStates
                breedName={breedName}
                breedId={breedId}
              />
            </>
          )}
          <BreederProfileBreedOverviewPages limit={10} />
        </div>
      </div>
    </>
  );
}

Component.propTypes = {
  expInfiniteScrollValue: PropTypes.string
};

Component.defaultProps = {
  expInfiniteScrollValue: 'control'
};
